import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Loader2, GitBranch } from "lucide-react";
import {
  useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, PeriodSelector, KpiTile, DrilldownPanel, fmtValue,
} from "./shared";
import { AiExecutiveSummary } from "./AiExecutiveSummary";

export default function SalesLeadershipDashboard() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    execService.sales({ ...params, period }).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [blocked, params, period]);
  useEffect(() => { load(); }, [load]);

  const onDrill = (metric, label) => setDrill({ metric, label });
  const funnel = data?.funnel || [];
  const maxVal = Math.max(1, ...funnel.map((f) => f.value));
  const radar = data?.radar || {};

  return (
    <div data-testid="exec-sales-page">
      <PageHeader icon={TrendingUp} title="Sales Leadership" subtitle="Pipeline health, conversion and deals needing attention.">
        <PeriodSelector period={period} setPeriod={setPeriod} />
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.tiles.map((t) => <KpiTile key={t.key} tile={t} onDrill={onDrill} />)}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5" data-testid="sales-funnel">
              <h2 className="text-sm font-bold text-foreground flex items-center gap-2 mb-3"><GitBranch className="h-4 w-4 text-primary" /> Pipeline by stage</h2>
              {funnel.length === 0 ? <p className="text-xs text-muted-foreground">No open opportunities.</p> : (
                <div className="space-y-2">
                  {funnel.map((f) => (
                    <div key={f.stage} data-testid={`funnel-${f.stage}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-foreground font-medium">{f.stage} <span className="text-muted-foreground">· {f.count}</span></span>
                        <span className="text-muted-foreground">{fmtValue(f.value, "currency")}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(f.value / maxVal) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <AiExecutiveSummary period={period} params={params} />
          </div>

          <RadarTable title="High-value open deals" rows={radar.highValue} testid="radar-high" />
          <RadarTable title="Closing soon (30 days)" rows={radar.closingSoon} testid="radar-soon" />
          <RadarTable title="Stale (past close date)" rows={radar.stale} testid="radar-stale" />
        </div>
      ) : null}

      {drill && <DrilldownPanel metric={drill.metric} label={drill.label} params={params} onClose={() => setDrill(null)} />}
    </div>
  );
}

function RadarTable({ title, rows, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid={testid}>
      <h2 className="text-sm font-bold text-foreground p-4 pb-2">{title}</h2>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-4 py-2">Opportunity</th><th className="px-4 py-2">Customer</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2">Close date</th></tr></thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-b border-border last:border-0">
              <td className="px-4 py-2 text-foreground">{o.name}</td>
              <td className="px-4 py-2 text-muted-foreground">{o.customerName}</td>
              <td className="px-4 py-2 text-muted-foreground">{o.stage}</td>
              <td className="px-4 py-2 text-right font-semibold text-foreground">{fmtValue(o.amount, "currency")}</td>
              <td className="px-4 py-2 text-muted-foreground">{o.expectedCloseDate ? String(o.expectedCloseDate).slice(0, 10) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
